import { Heart, Users, MapPin, Globe, Package, PlusCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { SEO } from '../components/SEO';
import { CampaignHighlight, CampaignHighlightData } from '../components/landing/CampaignHighlight';
import { ActiveCampaigns } from '../components/landing/ActiveCampaigns';
import { ImpactStats } from '../components/landing/ImpactStats';
import { VerificationProcess } from '../components/landing/VerificationProcess';
import { PlatformUpdates } from '../components/landing/PlatformUpdates';
import { AboutSummary } from '../components/landing/AboutSummary';
import { TrustIndicators } from '../components/landing/TrustIndicators';


// ─── Comunicado destacado actual (cambiar o poner null para desactivar) ───
const FEATURED_CAMPAIGN: CampaignHighlightData | null = {
  label: 'Comunicado',
  title: 'Nuestra solidaridad con Colombia',
  description:
    'El equipo de DonaApp se solidariza con las personas afectadas por el terremoto de magnitud 7.4 que sacudió a Colombia el 10 de agosto de 2026, con epicentro en Chocó y fuertes afectaciones en Cali, Pereira, Manizales y el Eje Cafetero. Seguimos trabajando cada día para conectar a quienes necesitan ayuda con quienes quieren darla — en Colombia y en cualquier parte del mundo.',
  location: 'Colombia',
  link: '/marketplace',
  ctaText: 'Explorar campañas activas',
  supportMessage: 'DonaApp es una plataforma abierta para cualquier causa, en cualquier país.',
};

export function HomePage() {
  return (
    <div className="space-y-16 py-12">
      <SEO />
      {/* Hero Section — 2 columnas en desktop, apilado en móvil */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-16">
          {/* Columna izquierda: texto */}
          <div className="flex-1 text-center lg:text-left">
            <img src="/imagotipo.png" alt="DonaApp" className="h-12 mb-6 mx-auto lg:mx-0" />
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Crea una campaña.
              <span className="text-indigo-600"> Ayuda a una causa.</span>
            </h1>
            <p className="text-lg text-gray-600 mb-6">
              DonaApp es la herramienta gratuita para organizar campañas de recolección de artículos
              y objetos para la causa que quieras ayudar: una fundación, una familia afectada, una
              comunidad en emergencia. Publica exactamente qué se necesita, comparte tu campaña, y
              mira el inventario actualizarse en tiempo real con cada donación recibida.
            </p>

            {/* Badge aclaratorio del modelo */}
            <div className="inline-flex items-center space-x-2 bg-indigo-50 border border-indigo-200 rounded-full px-4 py-2 mb-8">
              <Package className="w-4 h-4 text-indigo-600" />
              <span className="text-sm font-medium text-indigo-700">Donaciones en especie — sin transacciones de dinero</span>
            </div>

            {/* CTAs — primario: crear campaña, secundario: ver campañas */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
              <Link to="/register" className="px-6 py-3 text-base font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-colors flex items-center justify-center space-x-2">
                <PlusCircle className="w-4 h-4" />
                <span>Crear campaña gratis</span>
              </Link>
              <Link to="/marketplace" className="px-6 py-3 text-base font-medium text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-lg hover:bg-indigo-100 transition-colors flex items-center justify-center space-x-2">
                <Heart className="w-4 h-4" />
                <span>Ver campañas verificadas</span>
              </Link>
            </div>
            <p className="mt-4 text-xs text-gray-500">
              ¿Quieres ayudar directamente? Explora las campañas activas y dona lo que tengas.
            </p>
          </div>

          {/* Columna derecha: imagen hero */}
          <div className="flex-1 w-full max-w-md lg:max-w-none">
            <picture>
              <source
                media="(max-width: 767px)"
                srcSet="/donar-mobile.webp"
                type="image/webp"
              />
              <source
                srcSet="/donar.webp"
                type="image/webp"
              />
              <img
                src="/donar.png"
                alt="Persona entregando una caja de donaciones a otra persona"
                width={1586}
                height={992}
                fetchPriority="high"
                decoding="async"
                className="w-full h-auto rounded-xl shadow-lg"
              />
            </picture>
          </div>
        </div>
      </section>

      {/* Comunicado destacado (modular, se desactiva poniendo FEATURED_CAMPAIGN = null) */}
      <CampaignHighlight campaign={FEATURED_CAMPAIGN} />

      {/* Campañas activas reales desde el backend */}
      <ActiveCampaigns />

      {/* Cómo funciona */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            ¿Cómo funciona DonaApp?
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Nuestra plataforma hace que donar y recibir donaciones sea simple, 
            transparente y efectivo.
          </p>
        </div>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          <div className="text-center">
            <div className="bg-primary-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <Heart className="w-8 h-8 text-primary-600" />
            </div>
            <h3 className="text-xl font-semibold mb-2">Crea tu Campaña</h3>
            <p className="text-gray-600">
              Define qué productos necesitas y comparte tu historia con la comunidad.
            </p>
          </div>
          
          <div className="text-center">
            <div className="bg-success-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <Users className="w-8 h-8 text-success-600" />
            </div>
            <h3 className="text-xl font-semibold mb-2">Conecta Donantes</h3>
            <p className="text-gray-600">
              Los donantes pueden encontrar tu campaña y comprometerse con productos específicos.
            </p>
          </div>
          
          <div className="text-center">
            <div className="bg-warning-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <MapPin className="w-8 h-8 text-warning-600" />
            </div>
            <h3 className="text-xl font-semibold mb-2">Coordina Entregas</h3>
            <p className="text-gray-600">
              Información de ubicación clara para facilitar la entrega de donaciones.
            </p>
          </div>
          
          <div className="text-center">
            <div className="bg-secondary-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <Globe className="w-8 h-8 text-secondary-600" />
            </div>
            <h3 className="text-xl font-semibold mb-2">Tú decides quién ve tu campaña</h3>
            <p className="text-gray-600">
              Comparte tu campaña con quien quieras por link. Si eres una organización registrada, solicita verificación para aparecer en el marketplace público.
            </p>
          </div>
        </div>
      </section>

      {/* Cifras de impacto */}
      <ImpactStats />

      {/* Cómo verificamos las campañas */}
      <VerificationProcess />

      {/* Actualizaciones de DonaApp */}
      <PlatformUpdates />

      {/* Indicadores de seguridad y confianza */}
      <TrustIndicators />

      {/* Acerca de resumido */}
      <AboutSummary />

      {/* CTA final */}
      <section className="bg-primary-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <h2 className="text-3xl font-bold mb-4">
            Cada producto cuenta
          </h2>
          <p className="text-xl mb-8 text-primary-100">
            Ya sea que quieras donar lo que tienes o pedir lo que necesitas, estás en el lugar correcto.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/marketplace" className="px-6 py-3 text-base font-medium bg-white text-primary-700 rounded-lg hover:bg-gray-100 transition-colors">
              Explorar campañas
            </Link>
            <Link to="/register" className="px-6 py-3 text-base font-medium text-white border border-white/40 rounded-lg hover:bg-white/10 transition-colors">
              Crear mi campaña
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
